import React, { useState } from 'react'
import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core'
import { DealNotificationType, INotificationSubscriptionPreferences, INotificationSubscriptionSetting } from 'typescript/interfaces/deals'

interface Props {
  initialValues: INotificationSubscriptionSetting
  isActive?: boolean
  onChange: (notificationType: DealNotificationType, value: INotificationSubscriptionPreferences | null) => void,
  classes: any,
}

type NotificationSubscriptionPreferenceOptionsType = keyof INotificationSubscriptionPreferences;

const notificationTypeDescriptions = {
  [DealNotificationType.dealCollectedPercentage]: "Deal Percentage Collected",
  [DealNotificationType.dealWasFunded]: "Send the Email Confirmation that the deal was Funded",
}

const disabledOptionsByType: Record<
  DealNotificationType,
  NotificationSubscriptionPreferenceOptionsType[]
> = {
  [DealNotificationType.dealCollectedPercentage]: ['admin', 'representative'],
  [DealNotificationType.dealWasFunded]: [], 
};

const isDisabledOption = (
  option: NotificationSubscriptionPreferenceOptionsType,
  type: DealNotificationType
) => disabledOptionsByType[type]?.includes(option);

const checkboxOptions: {
  key: NotificationSubscriptionPreferenceOptionsType;
  label: string;
}[] = [
  { key: 'admin', label: 'Admin' },
  { key: 'representative', label: 'REP' },
  { key: 'iso', label: 'ISO' },
  { key: 'syndicator', label: 'Syndicator' },
  { key: 'merchant', label: 'Merchant' },
];

export default function NotificationSubscriptionForm({ initialValues, isActive = false, onChange, classes }: Props) {
  const { notificationType } = initialValues;
  
  const isRoleless = checkboxOptions.every(option => initialValues[option.key] === null)

  const [preferences, setPreferences] = useState<INotificationSubscriptionPreferences>(
    {
      admin: initialValues.admin,
      representative: initialValues.representative,
      iso: initialValues.iso,
      merchant: initialValues.merchant,
      syndicator: initialValues.syndicator
    }
  );

  const [isRolelessChecked, setIsRolelessChecked] = useState(isActive)

  const handlePreferenceChange = (key: NotificationSubscriptionPreferenceOptionsType, value: boolean) => {
    const newPreferences = {
      ...preferences,
      [key]: value
    };
    
    setPreferences(newPreferences);
    onChange(notificationType, newPreferences);
  }

  const handleRolelessChange = (isChecked: boolean) => {
    setIsRolelessChecked(isChecked);
    
    if (!isChecked) {
      onChange(notificationType, null); 
    } else {
      onChange(notificationType, {
        admin: null,
        representative: null,
        iso: null,
        merchant: null,
        syndicator: null,
      } as INotificationSubscriptionPreferences);
    }
  }

  return (
    <Box>
      {isRoleless ? (
        <Grid item xs={12}>
          <FormControlLabel
            classes={{ label: classes.checkboxLabel }}
            label={notificationTypeDescriptions[notificationType]}
            control={
              <Checkbox
                checked={isRolelessChecked}
                onChange={(e) => handleRolelessChange(e.target.checked)}
                color="primary"
              />
            }
          />
        </Grid>
      ) : (
        <>
          <Typography className={classes.notificationPreferencesLabel}>
            Select recipients to be notified about{' '}
            {notificationTypeDescriptions[notificationType]}:
          </Typography>

          {checkboxOptions.map(({ key, label }) => (
            <Grid item xs={12} key={key}>
              <FormControlLabel
                disabled={isDisabledOption(key, notificationType)}
                classes={{ label: classes.checkboxLabel }}
                label={label}
                control={
                  <Checkbox
                    checked={preferences[key] || false}
                    onChange={(e) => handlePreferenceChange(key, e.target.checked)}
                    color="primary"
                  />
                }
              />
            </Grid>
          ))}
        </>
      )}
    </Box>
  );
}